import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  OPHTHALMOLOGY_STANDARD_EXAMS,
  type OphthalmologyExamSelection,
  emptyOphthalmologySelections,
} from './ophthalmologyExamOrderConstants';

const HEADER_BG = '#cfe8fc';

interface OphthalmologyExamOrderSectionProps {
  selections: OphthalmologyExamSelection;
  onChangeSelections: (next: OphthalmologyExamSelection) => void;
  outrosText: string;
  onChangeOutros: (v: string) => void;
  observacoes: string;
  onChangeObservacoes: (v: string) => void;
  onCommit: () => void;
}

const OphthalmologyExamOrderSection: React.FC<OphthalmologyExamOrderSectionProps> = ({
  selections,
  onChangeSelections,
  outrosText,
  onChangeOutros,
  observacoes,
  onChangeObservacoes,
  onCommit,
}) => {
  const toggle = (key: string, field: 'od' | 'oe' | 'ao', aoOnly?: boolean) => {
    const cur = selections[key] || { od: false, oe: false, ao: false };
    if (aoOnly) {
      onChangeSelections({ ...selections, [key]: { od: false, oe: false, ao: !cur.ao } });
      return;
    }
    const next = { ...selections, [key]: { ...cur, [field]: !cur[field] } };
    onChangeSelections(next);
  };

  const hasSelection =
    OPHTHALMOLOGY_STANDARD_EXAMS.some((r) => {
      const s = selections[r.key];
      return s && (s.od || s.oe || s.ao);
    }) ||
    outrosText.trim().length > 0 ||
    observacoes.trim().length > 0;

  return (
    <Box>
      <Typography variant="body2" sx={{ color: '#475569', mb: 1.5 }}>
        Marque os exames solicitados para cada olho (OD direito, OE esquerdo, AO ambos).
      </Typography>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, mb: 2, borderColor: '#cbd5e1' }}
      >
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: HEADER_BG }}>
              <TableCell sx={{ fontWeight: 700, color: '#0f172a', width: '52%' }}>Exame</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: '#0f172a', width: '16%' }}>
                OD
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: '#0f172a', width: '16%' }}>
                OE
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: '#0f172a', width: '16%' }}>
                AO
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {OPHTHALMOLOGY_STANDARD_EXAMS.map((row) => {
              const s = selections[row.key] || { od: false, oe: false, ao: false };
              return (
                <TableRow key={row.key} hover sx={{ '& td': { borderColor: '#e2e8f0', py: 0.5 } }}>
                  <TableCell sx={{ fontWeight: 500, color: '#334155' }}>
                    {row.label}
                    {row.aoOnly && (
                      <Typography component="span" variant="caption" sx={{ color: '#94a3b8', ml: 0.75 }}>
                        (somente AO)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.aoOnly ? (
                      <Typography variant="caption" sx={{ color: '#cbd5e1' }}>—</Typography>
                    ) : (
                      <Checkbox
                        size="small"
                        checked={s.od}
                        onChange={() => toggle(row.key, 'od')}
                        sx={{ color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' } }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.aoOnly ? (
                      <Typography variant="caption" sx={{ color: '#cbd5e1' }}>—</Typography>
                    ) : (
                      <Checkbox
                        size="small"
                        checked={s.oe}
                        onChange={() => toggle(row.key, 'oe')}
                        sx={{ color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' } }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={s.ao}
                      onChange={() => toggle(row.key, 'ao', row.aoOnly)}
                      sx={{ color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ bgcolor: HEADER_BG }}>
              <TableCell colSpan={4} sx={{ fontWeight: 700, color: '#0f172a', py: 1 }}>
                Outros — especificar
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} sx={{ borderTop: 'none', pt: 0, pb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Exames não listados acima..."
                  value={outrosText}
                  onChange={(e) => onChangeOutros(e.target.value)}
                />
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: HEADER_BG }}>
              <TableCell colSpan={4} sx={{ fontWeight: 700, color: '#0f172a', py: 1 }}>
                Observações
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} sx={{ borderTop: 'none', pt: 0, pb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
                  Informações complementares para o laboratório ou centro de exames (ex.: lateralidade, protocolo).
                  Para prescrição de óculos, use a aba <strong>Refração (óculos)</strong>.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={3}
                  placeholder="Observações da solicitação..."
                  value={observacoes}
                  onChange={(e) => onChangeObservacoes(e.target.value)}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
        OD = olho direito · OE = olho esquerdo · AO = ambos os olhos
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Button
          variant="contained"
          disabled={!hasSelection}
          onClick={onCommit}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#2563eb' },
          }}
        >
          Adicionar solicitação à fila
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            onChangeSelections(emptyOphthalmologySelections());
            onChangeOutros('');
            onChangeObservacoes('');
          }}
          sx={{ textTransform: 'none', borderColor: '#cbd5e1', color: '#475569' }}
        >
          Limpar marcações
        </Button>
      </Box>
    </Box>
  );
};

export default OphthalmologyExamOrderSection;
